import { storageQueryKeys } from "@/Features/Storage/storage.query";
import { StorageAPI } from "@/Features/Storage/storage.api";
import {
  Option,
  SelectBox,
  TagBox,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";

interface StorageSelectorProps {
  caseId?: number;
  defaultValue?: string | string[];
  onChange?: (value: string | string[]) => void;
  error?: string;
  openOnFocus?: boolean;
  mode?: "single" | "multiple";
}

type StorageItem = {
  storage_id: number;
  storage_number: string;
  make?: string;
  model_name?: string;
  serial_number?: string;
  is_general_storage: number;
};

type StorageOption = {
  label: string;
  value: string;
  group: string;
  data: StorageItem;
};

const StorageOptionComponent = ({
  option,
}: {
  option: Partial<StorageOption>;
}) => {
  const theme = useTheme();
  const itemData = option.data;

  if (!itemData) {
    return option.label;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div>{option.data?.storage_number}</div>
      <div style={{ color: theme.palette.text.secondary }}>
        {[option.data?.make || "None", option.data?.model_name || "None"]
          .filter((i) => !!i)
          .join(" | ")}
      </div>
    </div>
  );
};

const StorageSelector: FC<StorageSelectorProps> = ({
  caseId,
  defaultValue,
  onChange,
  error,
  openOnFocus = false,
  mode = "single",
}) => {
  const { data: storageList } = useQuery<StorageItem[]>({
    queryKey: [
      ...storageQueryKeys.legacyLists,
      {
        case_id: caseId,
        active_only: true,
        include_general_storage: true,
      },
    ],
    queryFn: () =>
      StorageAPI.getStorage({
        case_id: caseId,
        active_only: true,
        include_general_storage: true,
        // Cast to the selector-local StorageItem shape, which narrows the
        // open-ended Features StorageItem fields this component reads.
      }) as Promise<StorageItem[]>,
    enabled: !!caseId,
  });

  // Create grouped options
  const mappedOptions =
    storageList?.map((storage) => ({
      label: `${storage.storage_number} - ${storage.make || "Unknown"} ${
        storage.model_name || "Unknown"
      }`,
      value: storage.storage_id.toString(),
      data: storage,
      group:
        storage.is_general_storage === 1
          ? "General Storage"
          : "Assigned Storage",
    })) || [];

  // Check if there are any assigned storage items
  const hasAssignedStorage = mappedOptions.some(
    (option) => option.group === "Assigned Storage"
  );

  // If no assigned storage items, add a placeholder
  const options = hasAssignedStorage
    ? mappedOptions
    : [
        ...mappedOptions,
        {
          label: "No storage assigned to case",
          value: "",
          group: "Assigned Storage",
          disabled: true,
        },
      ];

  if (mode === "multiple") {
    return (
      <TagBox
        defaultValue={defaultValue as string[]}
        size="sm"
        variant="outlined"
        label="Storage Items"
        placeholder="Select storage items..."
        description="Storage locations for these acquisitions."
        onChange={(value: unknown[]) => onChange?.(value as string[])}
        data={options}
        error={error}
        clearable={true}
        searchable
        allowCustomValue={false}
        openOnFocus={openOnFocus}
        grouped={true}
        renderOption={(item: Option | string) => {
          if (typeof item === "string") {
            return <div>{item}</div>;
          } else {
            return <StorageOptionComponent option={item} />;
          }
        }}
      />
    );
  }

  return (
    <SelectBox
      defaultValue={defaultValue as string}
      size="sm"
      variant="outlined"
      label="Storage"
      placeholder="Select storage item..."
      description="Storage location for this acquisition."
      onChange={onChange as (value: string) => void}
      data={options}
      error={error}
      clearable={true}
      searchable
      allowCustomValue={false}
      openOnFocus={openOnFocus}
      grouped={true}
      renderOption={(item: Option | string) => {
        if (typeof item === "string") {
          return <div>{item}</div>;
        } else {
          return <StorageOptionComponent option={item} />;
        }
      }}
    />
  );
};

export default StorageSelector;
